import { LitElement, html, css } from 'lit';
import { designSystem, componentStyles } from '../../styles/design-system.js';
import { settingsStyles } from '../../styles/settings-styles.js';
import { iconStyles } from '../../styles/icon-styles.js';
import { settingsService } from '../../services/settings-service.js';

export class SecuritySettings extends LitElement {
  static properties = {
    _redaction: { type: Object, state: true },
    _retentionRecordings: { state: true },
    _retentionTranscripts: { state: true },
    _saving: { state: true }
  };

  static styles = [designSystem, componentStyles, settingsStyles];

  constructor() {
    super();
    this._redaction = {
      creditCards: true,
      ssn: true,
      cvv: true,
      email: false,
      phone: false,
      addresses: false
    };
    this._retentionRecordings = '90';
    this._retentionTranscripts = '180';
    this._saving = false;
  }

  connectedCallback() {
    super.connectedCallback();
    this._load();
  }

  async _load() {
    try {
      const data = await settingsService.loadSecurityConfig();
      if (data.success && data.config) {
        const c = data.config;
        const toBool = (val, defOn) => {
          if (val === undefined || val === null || val === '') return defOn;
          return val === '1' || val === 1 || val === true;
        };
        this._redaction = {
          creditCards: toBool(c.voice_redaction_credit_cards, true),
          ssn: toBool(c.voice_redaction_ssn, true),
          cvv: toBool(c.voice_redaction_cvv, true),
          email: toBool(c.voice_redaction_email, false),
          phone: toBool(c.voice_redaction_phone, false),
          addresses: toBool(c.voice_redaction_addresses, false),
        };
        this._retentionRecordings = String(c.voice_retention_recordings || 90);
        this._retentionTranscripts = String(c.voice_retention_transcripts || 180);
      }
    } catch (err) {
      console.error('[SecuritySettings] Load failed:', err);
    }
  }

  _toggleRedaction(key) {
    this._redaction = { ...this._redaction, [key]: !this._redaction[key] };
  }

  async _save() {
    if (this._saving) return;
    this._saving = true;

    try {
      const payload = {
        voice_redaction_credit_cards: this._redaction.creditCards ? '1' : '0',
        voice_redaction_ssn: this._redaction.ssn ? '1' : '0',
        voice_redaction_cvv: this._redaction.cvv ? '1' : '0',
        voice_redaction_email: this._redaction.email ? '1' : '0',
        voice_redaction_phone: this._redaction.phone ? '1' : '0',
        voice_redaction_addresses: this._redaction.addresses ? '1' : '0',
        voice_retention_recordings: this._retentionRecordings,
        voice_retention_transcripts: this._retentionTranscripts,
      };

      const data = await settingsService.saveSecurityConfig(payload);
      if (data.success) {
        this.dispatchEvent(new CustomEvent('toast', { detail: { message: data.message || 'Security settings saved' }, bubbles: true, composed: true }));
        this.dispatchEvent(new CustomEvent('settings-saved', { bubbles: true, composed: true }));
      } else {
        this.dispatchEvent(new CustomEvent('toast', { detail: { message: data.error || 'Save failed', type: 'error' }, bubbles: true, composed: true }));
      }
    } catch (err) {
      this.dispatchEvent(new CustomEvent('toast', { detail: { message: 'Network error saving settings', type: 'error' }, bubbles: true, composed: true }));
    } finally {
      this._saving = false;
    }
  }

  render() {
    return html`
      ${iconStyles}
      <div class="content-header">
        <h2 class="content-title">Security & Privacy</h2>
        <p class="content-description">Configure transcript redaction rules and data retention policies to comply with PCI-DSS and privacy regulations.</p>
      </div>

      <div class="settings-section">
        <div class="section-title-row">
          <i class="fa-sharp-duotone fa-solid fa-shield-check section-icon"></i>
          <span class="section-title">Transcript Redaction</span>
        </div>
        <p class="section-description">Automatically detect and redact sensitive information from call transcripts.</p>

        <div class="redaction-grid">
          ${this._renderRedaction('creditCards', 'Credit Cards')}
          ${this._renderRedaction('ssn', 'SSN')}
          ${this._renderRedaction('cvv', 'CVV')}
          ${this._renderRedaction('email', 'Email')}
          ${this._renderRedaction('phone', 'Phone Numbers')}
          ${this._renderRedaction('addresses', 'Addresses')}
        </div>
      </div>

      <div class="settings-section">
        <div class="section-title-row">
          <i class="fa-sharp-duotone fa-solid fa-clock-rotate-left section-icon"></i>
          <span class="section-title">Data Retention</span>
        </div>
        <p class="section-description">Set how long data is kept before automatic deletion.</p>

        <div class="form-grid">
          <div class="form-group">
            <label class="form-label">Call Recordings Retention</label>
            <select class="form-select" .value=${this._retentionRecordings} @change=${e => this._retentionRecordings = e.target.value}>
              <option value="30">30 days</option>
              <option value="60">60 days</option>
              <option value="90">90 days</option>
              <option value="180">180 days</option>
            </select>
            <div class="form-hint">How long to keep call audio recordings.</div>
          </div>
          <div class="form-group">
            <label class="form-label">Transcripts Retention</label>
            <select class="form-select" .value=${this._retentionTranscripts} @change=${e => this._retentionTranscripts = e.target.value}>
              <option value="30">30 days</option>
              <option value="60">60 days</option>
              <option value="90">90 days</option>
              <option value="180">180 days</option>
            </select>
            <div class="form-hint">How long to keep call transcripts.</div>
          </div>
        </div>
      </div>

      <div class="actions-bar">
        <button class="btn btn-primary" @click=${this._save} ?disabled=${this._saving}>
          ${this._saving
            ? html`<i class="fa-sharp fa-light fa-spinner-third" style="animation:spin 0.6s linear infinite;"></i> Saving...`
            : html`<i class="fa-sharp fa-light fa-floppy-disk"></i> Save Changes`
          }
        </button>
      </div>
    `;
  }

  _renderRedaction(key, label) {
    return html`
      <div class="redaction-item">
        <div
          class="toggle ${this._redaction[key] ? 'active' : ''}"
          @click=${() => this._toggleRedaction(key)}
        ></div>
        <span class="redaction-label">${label}</span>
      </div>
    `;
  }
}

customElements.define('security-settings', SecuritySettings);
